import { Sun } from 'lucide-react'
import { Button } from '../../components/button'
import { Checkbox } from '../../components/checkbox'
import './styles.scss'
import { ColorPickerButton } from '../../components/buttonSelectionColor'
import { THEMES } from '../systemTheme/constants'
import { useColorPickerButton } from '../../hooks/colorPicker/useColorPickerButton'
import { useEffect, useState } from 'react'
import { Loader } from '../../components/loader'
import { COLORS, SCREENS_THEME } from './constants'
import { useTheme } from '../../hooks/setup/useTheme'
import { ThemeChoices } from '../systemTheme/types'
import { useCustomTheme } from '../../hooks/setup/useCustomTheme'
import { useSteps } from '../../hooks/setup/useSteps'
import { useGenerateSuggestionsMutation } from '../../store/modules/theme/api'
import { ICustomTheme } from './types'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { ThemeSuggestionColors } from '../../store/modules/theme/types'

export const SystemCustomTheme = () => {
  const { nextStep, previousStep } = useSteps()
  const { register: registerCustomTheme } = useCustomTheme()
  const { getData: getDataTheme } = useTheme()
  const {
    colors,
    handleSaveColor,
    handlerApplySuggestion,
    handlerResetSuggestion,
  } = useColorPickerButton()
  const [
    handlerGenerateColors,
    { data: generateColorsData, isLoading: isLoadingGenerateColor },
  ] = useGenerateSuggestionsMutation()
  const { getItem } = useLocalStorage()

  const [customTheme, setCustomTheme] = useState<ICustomTheme>({
    appearance: 'light',
    theme: '',
    themeID: null,
    colors: {},
  })
  const [suggestionColor, setSuggestionColor] = useState<
    'load' | 'applied' | 'open' | 'error'
  >('load')
  const [currentScreen, setCurrentScreen] = useState('default')

  useEffect(() => {
    const load = async () => {
      const themeSelected = await getDataTheme()

      if (!themeSelected) {
        await previousStep()
      }

      setCustomTheme((prevState) => ({
        ...prevState,
        theme: THEMES.find((theme) => theme.id === themeSelected?.theme)
          ?.title as string,
        themeID: themeSelected?.theme as ThemeChoices,
      }))
    }

    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!customTheme.themeID) return

    COLORS[customTheme.themeID].forEach((color) => {
      handleSaveColor(color.key, color.defaultColor)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customTheme.themeID])

  useEffect(() => {
    if (!customTheme.themeID) return

    const LoadGenerateColors = async () => {
      const currentTheme =
        customTheme.themeID === ThemeChoices.THEME_ID_OBSIDIAN
          ? 'theme_default'
          : `theme_${customTheme.themeID}`
      const colors = getItem('draftColorsLogo') as ThemeSuggestionColors

      setSuggestionColor('load')
      handlerGenerateColors({ theme_key: currentTheme, colors })
        .unwrap()
        .then(() => setSuggestionColor('open'))
        .catch(() => setSuggestionColor('error'))
    }

    LoadGenerateColors()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customTheme.themeID])

  const handlerSuggestion = () => {
    if (suggestionColor === 'open') return

    setSuggestionColor('open')
  }

  const handlerOnChangeColor = (key: string, color: string) => {
    handlerSuggestion()
    handleSaveColor(key, color)
  }

  const handlerOnSubmit = async () => {
    await registerCustomTheme(colors)
    await nextStep()
  }

  const renderSuggestion = () => {
    if (!isLoadingGenerateColor && !generateColorsData?.suggestions.length)
      return <></>

    switch (suggestionColor) {
      case 'load':
        return (
          <div className="content-load-suggestion">
            <Loader size={15} />
            <span className="content-info-load">
              Carregando sugestões de cores...
            </span>
          </div>
        )
      case 'open':
        return (
          <div className="content-info-suggestion-to-apply">
            <span className="title-info-suggestion">
              <strong style={{ color: '#6204e5' }}>
                {generateColorsData?.suggestions.length}
              </strong>{' '}
              sugestão de cores
            </span>

            <span
              className="btn-to-applied"
              onClick={() => {
                setSuggestionColor('applied')
                if (generateColorsData?.suggestions.length)
                  handlerApplySuggestion(
                    generateColorsData?.suggestions[0].light
                  )
              }}
            >
              Aplicar
            </span>
          </div>
        )
      case 'applied':
        return (
          <div className="content-info-suggestion-to-apply">
            <span className="title-info-suggestion">Sugestão aplicada</span>

            <span
              onClick={() => {
                setSuggestionColor('open')
                handlerResetSuggestion()
              }}
              className="btn-to-applied"
            >
              Reverter
            </span>
          </div>
        )
      default:
        return <></>
    }
  }

  const getCurrentScreen = () => {
    if (!customTheme.themeID) return

    const screens = SCREENS_THEME[customTheme.themeID]
    if (currentScreen in screens)
      return SCREENS_THEME[customTheme.themeID][currentScreen]

    return SCREENS_THEME[customTheme.themeID]['default']
  }

  const RenderCurrentScreen = getCurrentScreen()

  return (
    <div className="container-main-system-custom-theme">
      <div className="content-title">
        <strong>Confirmar Personalização</strong>

        <p>
          Ajuste as cores de cada elemento e veja como seu sistema vai ficar.
        </p>
      </div>

      <div className="container-enter-data">
        <div className="content-custom">
          <div className="content-side-option-to-custom-theme">
            <div className="selected-theme">
              <span className="label">Tema Selecionado</span>

              <div className="item-theme">
                <strong>{customTheme.theme}</strong>
                <Checkbox checked />
              </div>
            </div>

            <div className="selected-appearance">
              <span className="label">Aparência</span>

              <div className="content-appearance">
                <div className="item-appearance selected light">
                  <Sun className="icon-selected" />
                  <span>Claro</span>
                </div>

                {/* <div className='item-appearance dark'>
                  <Moon className='' />
                  <span>Escuro</span>
                </div> */}
              </div>
            </div>

            <div className="selected-colors">
              <div className="label content-label">
                <span>Cores</span>

                {renderSuggestion()}
              </div>

              <div className="container-colors">
                {customTheme.themeID &&
                  COLORS[customTheme.themeID]?.map((color) => {
                    return (
                      <div className="item-color" key={color.key}>
                        <span className="name-color">{color.name}</span>

                        <div className="set-color">
                          <span>{colors[color.key]?.toUpperCase()}</span>

                          <ColorPickerButton
                            buttonClassName="set-color-btn"
                            onChange={handlerOnChangeColor}
                            color={colors[color.key]}
                            onFocus={(key) => setCurrentScreen(key)}
                            id={color.key}
                          />
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>

            <div className="content-btn-submit">
              <Button
                onClick={handlerOnSubmit}
                type="button"
                fullscreen
                className="btn-submit"
              >
                Aplicar Tema
              </Button>
            </div>
          </div>

          <div className="content-screen-preview">
            {RenderCurrentScreen && <RenderCurrentScreen {...colors} />}
          </div>
        </div>
      </div>
    </div>
  )
}
